<template>
  <section class="certifications">
    <div class="certifications__header-wrapper">
      <h1 class="certifications__header">
        Certifications
      </h1>
      <div>
        <p class="certifications__header-subtitle">
          Enter the certification ID below to verify a certification.
        </p>
        <CertificationLookup />
      </div>
    </div>
    <template v-if="isPageLoading">
      <div
        class="certifications__certification-list certifications__certification-loaders"
      >
        <CertificationTileSkel />
        <CertificationTileSkel />
      </div>
    </template>
    <div class="certifications__certification__type">
      <div v-if="!isPageLoading" class="certifications__certification-list">
        <template v-for="cert in filteringWPTCertifications">
          <CertificationTile
            v-if="!cert.is_external"
            :key="cert.id"
            :certification="cert"
            :lab="practiceLab(cert.id)"
          />
          <SimpleCertificationTile
            v-else
            :key="cert.id"
            :certification="cert"
            :showDetails="false"
            :lab="practiceLab(cert.id)"
          />
        </template>
      </div>
    </div>
  </section>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import CertificationTileSkel from '@/components/loaders/skeletons/certifications/CertificationTileSkel.vue'
import CertificationTile from '@/components/certifications/CertificationTile.vue'
import SimpleCertificationTile from '@/components/certifications/SimpleCertificationTile.vue'
import CertificationLookup from '@/components/certifications/CertificationLookup.vue'
import { certLatestVersion } from '@/helpers'
import CONSTANTS from '@/constants'

export default {
  name: 'Certifications',
  components: {
    CertificationTileSkel,
    CertificationTile,
    SimpleCertificationTile,
    CertificationLookup,
  },
  data() {
    return {
      labCertMap: [],
    }
  },
  computed: {
    ...mapState({
      isPageLoading: state => state.app.isPageLoading,
      allCertifications: state => state.certifications.certificationList,
      labs: state => state.content.currentLabs,
    }),
    ...mapGetters({
      orderedListOfCertifications: 'certifications/orderedListOfCertifications',
      accredibleDisabled: 'featureFlags/accredibleDisabled',
    }),
    certificationList() {
      const certifications = this.allCertifications.filter(
        cert => !cert.is_external
      )
      return certifications
    },
    filteringWPTCertifications() {
      // filter beta certifications from the list of certifications if the user don't have an active voucher to take the exam
      // ToDo make this function more general tied to a data structure
      let filterOldEWPT = false
      return this.orderedListOfCertifications.filter(cert => {
        const isBeta = cert.versions[0].is_beta
        const hasActiveVouchers = cert.user_status?.active_vouchers?.length > 0
        if (isBeta && hasActiveVouchers) {
          if (cert.id === CONSTANTS.CERTIFICATIONS.IDS.eWPT) {
            filterOldEWPT = true
            return true
          }
        } else if (isBeta && !hasActiveVouchers) {
          return false
        } else {
          if (
            cert.id === CONSTANTS.CERTIFICATIONS.IDS.eWPT_old &&
            filterOldEWPT
          ) {
            return false
          }
          return true
        }
      })
    },
  },
  async mounted() {
    this.$store.dispatch('app/setIsComponentLoading', false)
    this.$store.dispatch('app/setIsPageLoading', true)
    await this.$store.dispatch('certifications/getAllCertifications')
    this.$store.dispatch('app/setIsPageLoading', false)
    this.fetchLabs()
  },
  methods: {
    async fetchLabs() {
      this.labCertMap = Object.assign(
        {},
        ...this.certificationList.map(cert => {
          const version = certLatestVersion(cert)
          return {
            [cert.id]: version.practice_lab
              ? version.practice_lab
              : version.questions_lab,
          }
        })
      )
      const labsToGet = Object.values(this.labCertMap)
      if (labsToGet && labsToGet.length > 0) {
        await this.$store.dispatch('content/getLabs', {
          labIds: labsToGet,
        })
      }
    },
    practiceLab(certificationId) {
      const labId = this.labCertMap[certificationId]
      return this.labs.find(l => l.id === labId)
    },
  },
}
</script>

<style lang="scss" scoped>
.certifications {
  padding: 125px 3% 50px 3%;
  margin: 0 auto;
  background-image: url('../assets/images/certification/full-background.jpg');
  background-size: cover;
  min-height: 100vh;

  @include tablet {
    padding: 125px 1% 150px 1%;
  }
  @include desktop {
    padding: 125px 25px 150px 25px;
  }
  @include lg-desktop {
    padding: 125px 150px 150px 150px;
  }
  &__header-wrapper {
    text-align: center;
    margin-bottom: 32px;
    @include tablet {
      margin-bottom: 85px;
    }
  }
  &__header {
    font-size: 40px;
    line-height: 52px;
    color: var(--white);
    margin: 0;
    &-subtitle {
      font-size: 20px;
      line-height: 24px;
      color: var(--light-blue-gray);
      text-align: center;
      max-width: 1000px;
      margin: 20px auto;
      font-feature-settings: 'clig' off, 'liga' off;

      @include tablet {
        font-size: 24px;
        line-height: 32px;
      }
    }
  }
  &__certification {
    &-list {
      display: flex;
      flex-wrap: wrap;
      align-items: flex-start;
      max-width: 1400px;
      gap: 10px;
      justify-content: center;
      @include mobile {
        gap: 20px;
        justify-content: flex-start;
      }
    }
    &-loaders {
      @include mobile {
        gap: 20px;
        justify-content: center;
        margin: auto;
      }
    }
    &__type {
      @include desktop {
        margin: auto;
        max-width: 1400px;
      }
      &-title {
        color: var(--white);
        font-size: rem(14px);
        line-height: rem(20px);
        margin-top: 28px;
        margin-bottom: 10px;
        padding-left: 5px;

        @include mobile {
          font-size: rem(18px);
          line-height: rem(24px);
        }
        @include tablet {
          margin-top: 35px;
          margin-bottom: 20px;
        }
      }
    }
  }

  &__empty {
    display: flex;
    justify-content: center;
    flex-direction: column;
    margin-bottom: 0px;

    &-img {
      width: 100%;
      height: 100%;
      object-fit: contain;

      @include tablet {
        width: unset;
        height: unset;
      }
    }
  }
}
</style>
